import { useCallback } from "react";
import { gql, useMutation } from "@apollo/client";

import { Maybe, VcsProvider } from "types/generated";
import useTypedContext from "hooks/useTypedContext";
import FlashContext from "components/FlashMessages/FlashContext";

import { MULTIPLE_VCS_PROVIDERS } from "./constants";
import {
  TEST_AZURE_DEVOPS_INTEGRATION,
  TEST_BITBUCKET_CLOUD_INTEGRATION,
  TEST_BITBUCKET_DATACENTER_INTEGRATION,
  TEST_GITHUB_INTEGRATION,
  TEST_GITLAB_INTEGRATION,
} from "./gql";

type UseTestVCSIntegrationConnectionProps = {
  provider: VcsProvider;
  id: Maybe<string>;
  name: string;
};

const MUTATION_BY_PROVIDER = {
  [MULTIPLE_VCS_PROVIDERS.AZURE_DEVOPS]: TEST_AZURE_DEVOPS_INTEGRATION,
  [MULTIPLE_VCS_PROVIDERS.BITBUCKET_CLOUD]: TEST_BITBUCKET_CLOUD_INTEGRATION,
  [MULTIPLE_VCS_PROVIDERS.BITBUCKET_DATACENTER]: TEST_BITBUCKET_DATACENTER_INTEGRATION,
  [MULTIPLE_VCS_PROVIDERS.GITHUB_ENTERPRISE]: TEST_GITHUB_INTEGRATION,
  [MULTIPLE_VCS_PROVIDERS.GITHUB]: TEST_GITHUB_INTEGRATION,
  [MULTIPLE_VCS_PROVIDERS.GITLAB]: TEST_GITLAB_INTEGRATION,
};

// The "Test connection" action is hidden for providers with no entry above, but hooks
// still run on every render, and useMutation(undefined) throws - hence the no-op fallback.
const NOOP_TEST_MUTATION = gql`
  mutation NoopTestVCSIntegrationConnection {
    __typename
  }
`;

const useTestVCSIntegrationConnection = ({
  provider,
  id,
  name,
}: UseTestVCSIntegrationConnectionProps) => {
  const { onError, reportSuccess } = useTypedContext(FlashContext);

  const testMutation = MUTATION_BY_PROVIDER[provider as keyof typeof MUTATION_BY_PROVIDER];

  const [testIntegrationConnection, { loading }] = useMutation(testMutation ?? NOOP_TEST_MUTATION);

  const handleTestIntegrationConnection = useCallback(() => {
    // The no-op document would resolve and report a false success.
    if (!testMutation) {
      return;
    }

    testIntegrationConnection({
      variables: {
        id: id ?? null,
      },
    })
      .then(({ data }) => {
        if (data) {
          reportSuccess({ message: `Connecting to ${name} succeeded` });
        }
      })
      .catch(onError);
  }, [id, name, onError, reportSuccess, testIntegrationConnection, testMutation]);

  return {
    testIntegrationConnection: handleTestIntegrationConnection,
    loading,
  };
};

export default useTestVCSIntegrationConnection;
